import { type FormEvent, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router";
import { useApi } from "../../app/ApiProvider";
import { useAuth } from "../../app/AuthProvider";
import { landingFor } from "../../app/navigation";
import { useAction } from "../../app/useAsync";
import { useSite } from "../../sites/SiteRoot";
import { agentFilterAttributesForValue } from "../end-user/agent-filter";
import {
	Button,
	Callout,
	Card,
	Field,
	LinkButton,
	PageHeader,
	PasswordInput,
	TextArea,
	TextInput,
} from "../../ui/primitives";
import { recoveryFor } from "../../ui/states";

function AuthFrame({
	title,
	description,
	children,
}: {
	title: string;
	description?: string;
	children: React.ReactNode;
}) {
	return (
		<div className="bg-[radial-gradient(circle_at_top_left,var(--color-brand-50),transparent_45%)] px-4 py-10 sm:py-14 lg:px-8 lg:py-20">
			<div className="mx-auto grid max-w-4xl overflow-hidden rounded-[24px] border border-ink-200/90 bg-white shadow-[0_24px_80px_rgb(13_17_23_/_0.10)] md:grid-cols-[0.9fr_1.1fr]">
				<div className="hidden bg-ink-950 p-8 text-white md:flex md:flex-col md:justify-between">
					<Link to="/" className="text-[21px] font-black tracking-[-0.03em] text-white">
						Agentry
					</Link>
					<div className="py-10">
						<p className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand-300">
							AI Agent Platform
						</p>
						<p className="mt-3 text-[25px] font-bold leading-snug tracking-tight">
							専門Agentと、仕事を
							<br />
							前へ進める場所。
						</p>
						<p className="mt-4 text-[13.5px] leading-relaxed text-ink-300">
							依頼、確認、成果物をひとつのワークスペースで管理できます。
						</p>
					</div>
					<p className="text-[11.5px] text-ink-400">安全な接続でアカウントを保護します</p>
				</div>
				<div className="p-6 sm:p-9 lg:p-11">
					<Link
						to="/"
						className="mb-7 block text-[19px] font-black tracking-tight text-ink-900 md:hidden"
					>
						Agentry
					</Link>
					<p className="text-[11px] font-bold uppercase tracking-[0.16em] text-brand-600">
						Agentry Account
					</p>
					<h1 className="mt-1 text-[25px] font-bold tracking-tight text-ink-950">{title}</h1>
					{description && <p className="mt-1.5 text-[14px] text-ink-600">{description}</p>}
					<div className="mt-6">{children}</div>
				</div>
			</div>
		</div>
	);
}

/** Error表示はreasonベースの復旧文言を使い、生のMessageを直接出さない。 */
function FormError({ error }: { error: unknown }) {
	if (!error) return null;
	const recovery = recoveryFor(error);
	return (
		<div
			role="alert"
			className="mt-3 rounded-lg border border-danger-500/40 bg-danger-100 px-3 py-2"
		>
			<p className="text-[13px] font-bold text-danger-600">{recovery.title}</p>
			<p className="text-[12.5px] text-ink-700">{recovery.hint}</p>
		</div>
	);
}

export function LoginPage() {
	const { login, state, role } = useAuth();
	const { mode } = useApi();
	const navigate = useNavigate();
	const [params] = useSearchParams();
	const next = safeNext(params.get("next"));
	const [email, setEmail] = useState("user@example.test");
	const [password, setPassword] = useState("password123");
	const action = useAction(login);

	if (state.status === "authenticated") {
		return <Navigate to={next ?? landingFor(role)} replace />;
	}

	async function onSubmit(event: FormEvent) {
		event.preventDefault();
		const session = await action.run(email, password);
		if (session) navigate(next ?? landingFor(session.membership.role), { replace: true });
	}

	return (
		<AuthFrame title="ログイン" description="登録済みのメールアドレスでログインします。">
			{params.get("reason") === "logged-out" && (
				<div className="mb-5">
					<Callout tone="success" title="ログアウトしました">
						この端末でのセッションを安全に終了しました。
					</Callout>
				</div>
			)}
			{params.get("reason") === "auth-required" && (
				<div className="mb-5">
					<Callout tone="brand" title="続けるにはログインが必要です">
						セッションの有効期限が切れた場合も、もう一度ログインすると元の画面へ戻れます。
					</Callout>
				</div>
			)}
			<form onSubmit={onSubmit} className="space-y-4">
				<Field label="メールアドレス">
					<TextInput
						type="email"
						autoComplete="email"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						required
					/>
				</Field>
				<Field label="パスワード">
					<PasswordInput
						autoComplete="current-password"
						value={password}
						onChange={(e) => setPassword(e.target.value)}
						required
					/>
				</Field>
				<FormError error={action.error} />
				<Button
					type="submit"
					className="w-full"
					disabled={action.pending}
					aria-busy={action.pending}
				>
					{action.pending ? "確認しています…" : "ログイン"}
				</Button>
			</form>

			<div className="mt-4 flex justify-between text-[13px]">
				<Link to="/password-reset" className="text-ink-600 hover:text-brand-600">
					パスワードを忘れた
				</Link>
				<Link to="/signup" className="font-semibold text-brand-600 hover:underline">
					新規登録
				</Link>
			</div>

			{mode === "mock" && (
				<div className="mt-5">
					<Callout tone="neutral" title="Mockモードのテストアカウント">
						<ul className="mt-1 space-y-0.5 font-mono text-[11.5px]">
							<li>user@example.test — Tenant User</li>
							<li>admin@example.test — Tenant Admin</li>
							<li>system@agentry.test — Agentry System Admin</li>
							<li>other@other.test — 別Tenant User</li>
						</ul>
						<p className="mt-1">いずれもパスワードは password123 です。</p>
					</Callout>
				</div>
			)}
		</AuthFrame>
	);
}

export function safeNext(value: string | null): string | null {
	if (!value?.startsWith("/") || value.startsWith("//")) return null;
	return value;
}

export function SignupPage() {
	const { signup, state, role } = useAuth();
	const site = useSite();
	const navigate = useNavigate();
	const [form, setForm] = useState({ name: "", email: "", password: "" });
	const action = useAction(signup);

	if (state.status === "authenticated") return <Navigate to={landingFor(role)} replace />;

	async function onSubmit(event: FormEvent) {
		event.preventDefault();
		const session = await action.run({
			...form,
			attributes: agentFilterAttributesForValue(site.agentFilterValue),
		});
		if (session) navigate(landingFor(session.membership.role), { replace: true });
	}

	return (
		<AuthFrame title="新規登録" description="登録後すぐにAgentへ依頼できます。">
			<form onSubmit={onSubmit} className="space-y-4">
				<Field label="お名前">
					<TextInput
						value={form.name}
						onChange={(e) => setForm({ ...form, name: e.target.value })}
						required
					/>
				</Field>
				<Field label="メールアドレス">
					<TextInput
						type="email"
						autoComplete="email"
						value={form.email}
						onChange={(e) => setForm({ ...form, email: e.target.value })}
						required
					/>
				</Field>
				<Field label="パスワード" hint="8文字以上">
					<TextInput
						type="password"
						autoComplete="new-password"
						minLength={8}
						value={form.password}
						onChange={(e) => setForm({ ...form, password: e.target.value })}
						required
					/>
				</Field>
				<FormError error={action.error} />
				<Button type="submit" className="w-full" disabled={action.pending}>
					{action.pending ? "登録しています…" : "登録して始める"}
				</Button>
				<p className="text-[12px] text-ink-500">
					登録すると
					<Link to="/legal/terms" className="text-brand-600 hover:underline">
						利用規約
					</Link>
					と
					<Link to="/legal/privacy" className="text-brand-600 hover:underline">
						プライバシーポリシー
					</Link>
					に同意したものとみなされます。
				</p>
			</form>
		</AuthFrame>
	);
}

export function PasswordResetPage() {
	const { client } = useApi();
	const [email, setEmail] = useState("");
	const action = useAction((value: string) => client.requestPasswordReset(value));

	return (
		<AuthFrame
			title="パスワード再設定"
			description="登録済みのメールアドレス宛に再設定用のリンクを送ります。"
		>
			{action.result ? (
				<Callout tone="success" title="送信しました">
					入力されたメールアドレスが登録されている場合、再設定用のリンクを送信しました。
					登録の有無はお答えしていません。
				</Callout>
			) : (
				<form
					onSubmit={(event) => {
						event.preventDefault();
						void action.run(email);
					}}
					className="space-y-4"
				>
					<Field label="メールアドレス">
						<TextInput
							type="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							required
						/>
					</Field>
					<FormError error={action.error} />
					<Button type="submit" className="w-full" disabled={action.pending}>
						{action.pending ? "送信しています…" : "再設定リンクを送る"}
					</Button>
				</form>
			)}
			<div className="mt-4 text-center text-[13px]">
				<Link to="/login" className="text-ink-600 hover:text-brand-600">
					ログインへ戻る
				</Link>
			</div>
		</AuthFrame>
	);
}

export function InviteJoinPage() {
	const { token = "" } = useParams();
	const { acceptInvite, state, role } = useAuth();
	const navigate = useNavigate();
	const [form, setForm] = useState({ name: "", password: "" });
	const action = useAction((input: { name: string; password: string }) =>
		acceptInvite(token, input),
	);

	if (state.status === "authenticated") return <Navigate to={landingFor(role)} replace />;

	async function onSubmit(event: FormEvent) {
		event.preventDefault();
		const session = await action.run(form);
		if (session) navigate(landingFor(session.membership.role), { replace: true });
	}

	return (
		<AuthFrame
			title="招待から参加"
			description="組織に招待されています。参加情報を入力してください。"
		>
			<p className="mb-4 font-mono text-[11.5px] text-ink-400">招待コード: {token}</p>
			<form onSubmit={onSubmit} className="space-y-4">
				<Field label="お名前">
					<TextInput
						value={form.name}
						onChange={(e) => setForm({ ...form, name: e.target.value })}
						required
					/>
				</Field>
				<Field label="パスワード" hint="8文字以上">
					<TextInput
						type="password"
						minLength={8}
						value={form.password}
						onChange={(e) => setForm({ ...form, password: e.target.value })}
						required
					/>
				</Field>
				<FormError error={action.error} />
				<Button type="submit" className="w-full" disabled={action.pending}>
					{action.pending ? "参加しています…" : "参加する"}
				</Button>
			</form>
		</AuthFrame>
	);
}

// ---------------------------------------------------------------------------
// Demo / Contact
// ---------------------------------------------------------------------------

const DEMO_SCRIPT = [
	{
		role: "agent" as const,
		text: "美容クリニックLP制作を担当します。まず今回のLPで最も増やしたい成果を確認させてください。",
	},
	{ role: "user" as const, text: "Web予約を増やしたいです。" },
	{
		role: "agent" as const,
		text: "Web予約を最優先のConversionとして設定しました。次に訴求する主要メニューと価格を教えてください。税込・回数・適用条件を含めてください。",
	},
	{
		role: "user" as const,
		text: "全身脱毛5回 148,000円（税込）。初回限定29,800円で、9月末までです。",
	},
	{
		role: "agent" as const,
		text: "初回限定価格は期間・回数・適用条件の明示が必要です。「9月末まで」「初回来院の方のみ」を併記する前提で構成します。次に主なTarget像を教えてください。",
	},
];

export function DemoPage() {
	const [visible, setVisible] = useState(2);
	const finished = visible >= DEMO_SCRIPT.length;

	return (
		<div className="mx-auto max-w-3xl px-4 py-10 lg:px-8">
			<PageHeader
				eyebrow="デモ"
				title="ログインせずに、進み方だけ見てみる"
				description="実際のGuided Chatと同じ流れです。Agentが順に質問し、業種のルールを踏まえて確認します。"
			/>
			<Card>
				<div className="space-y-3">
					{DEMO_SCRIPT.slice(0, visible).map((line, index) => (
						<div
							// biome-ignore lint/suspicious/noArrayIndexKey: 固定台本で並び替えが起きない
							key={index}
							className={line.role === "agent" ? "flex gap-2" : "flex justify-end"}
						>
							{line.role === "agent" && (
								<span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-600 text-[11px] font-bold text-white">
									A
								</span>
							)}
							<p
								className={
									line.role === "agent"
										? "max-w-lg rounded-2xl rounded-tl-sm bg-ink-100 px-3 py-2 text-[14px] leading-relaxed text-ink-800"
										: "max-w-lg rounded-2xl rounded-tr-sm bg-brand-600 px-3 py-2 text-[14px] leading-relaxed text-white"
								}
							>
								{line.text}
							</p>
						</div>
					))}
				</div>
				<div className="mt-5 flex flex-wrap gap-2 border-t border-ink-100 pt-4">
					{finished ? (
						<>
							<LinkButton to="/signup">続きを自分の案件で試す</LinkButton>
							<Button variant="secondary" onClick={() => setVisible(2)}>
								最初から見る
							</Button>
						</>
					) : (
						<Button onClick={() => setVisible((v) => v + 1)}>次のやりとりを見る</Button>
					)}
				</div>
			</Card>
			<Callout tone="neutral" title="デモでできないこと">
				デモでは実際のAI実行、成果物の生成、保存は行いません。登録すると、質問への回答から成果物確定まで進められます。
			</Callout>
		</div>
	);
}

export function ContactPage() {
	const [sent, setSent] = useState(false);
	const [form, setForm] = useState({ name: "", email: "", company: "", subject: "", body: "" });

	return (
		<div className="mx-auto max-w-2xl px-4 py-10 lg:px-8">
			<PageHeader
				eyebrow="お問い合わせ"
				title="導入のご相談"
				description="通常2営業日以内にご返信します。"
			/>
			{sent ? (
				<Callout tone="success" title="送信しました">
					お問い合わせありがとうございます。担当者よりご連絡いたします。
				</Callout>
			) : (
				<Card>
					<form
						className="space-y-4"
						onSubmit={(event) => {
							event.preventDefault();
							setSent(true);
						}}
					>
						<Field label="お名前">
							<TextInput
								value={form.name}
								onChange={(e) => setForm({ ...form, name: e.target.value })}
								required
							/>
						</Field>
						<Field label="メールアドレス">
							<TextInput
								type="email"
								value={form.email}
								onChange={(e) => setForm({ ...form, email: e.target.value })}
								required
							/>
						</Field>
						<Field label="会社名">
							<TextInput
								value={form.company}
								onChange={(e) => setForm({ ...form, company: e.target.value })}
							/>
						</Field>
						<Field label="件名">
							<TextInput
								value={form.subject}
								onChange={(e) => setForm({ ...form, subject: e.target.value })}
								required
							/>
						</Field>
						<Field label="お問い合わせ内容">
							<TextArea
								rows={6}
								value={form.body}
								onChange={(e) => setForm({ ...form, body: e.target.value })}
								required
							/>
						</Field>
						<Button type="submit">送信する</Button>
					</form>
				</Card>
			)}
		</div>
	);
}
